import { ComponentService } from "../Services/Axios/Api";
import { generateDynamicCSS } from "../ComponentsMap";

export const BUTTON_TYPE_NORMAL = 'normal';
export const BUTTON_TYPE_WHATSAPP = 'whatsapp';

export const buildWhatsAppLink = (phoneNumber, helpText, pageUrl = '') => {
    const phone = String(phoneNumber || '').replace(/\D/g, '');
    if (!phone) return '#';

    const currentPageUrl = pageUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const message = `${helpText || ''}\n${currentPageUrl}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

export const createDefaultFloatingButtonItem = () => ({
    text: '',
    buttonType: BUTTON_TYPE_NORMAL,
    link: '/',
    phoneNumber: '',
    helpText: '',
    icon: '',
    isVisible: true,
    position: 'right',
    style: {
        FloatingDonateButton: {
            backgroundColor: "#ddd",
            color: "black",
            right: "20px",
        },
        FloatingDonateButtonHover: {
            backgroundColor: "#ddd",
            color: "black",
        }
    }
});

const normalizeIcon = (icon) => {
    if (icon == null || String(icon).trim() === '') return null;
    return String(icon).replace(/^fa-/, '');
};

const normalizeButtonType = (buttonType) =>
    buttonType === BUTTON_TYPE_WHATSAPP ? BUTTON_TYPE_WHATSAPP : BUTTON_TYPE_NORMAL;

const normalizeButtonItem = (button) => ({
    ...createDefaultFloatingButtonItem(),
    ...button,
    buttonType: normalizeButtonType(button?.buttonType),
    phoneNumber: button?.phoneNumber ?? '',
    helpText: button?.helpText ?? '',
    icon: normalizeIcon(button?.icon),
    style: {
        ...createDefaultFloatingButtonItem().style,
        ...button?.style,
        FloatingDonateButton: {
            ...createDefaultFloatingButtonItem().style.FloatingDonateButton,
            ...button?.style?.FloatingDonateButton,
        },
        FloatingDonateButtonHover: {
            ...createDefaultFloatingButtonItem().style.FloatingDonateButtonHover,
            ...button?.style?.FloatingDonateButtonHover,
        },
    },
});

const normalizeFloatingButtonData = (data) => {
    if (!data || typeof data !== 'object') {
        return { buttons: [createDefaultFloatingButtonItem()] };
    }

    if (Array.isArray(data.buttons)) {
        return {
            id: data.id,
            ComponentName: data.ComponentName || 'FloatingButton',
            buttons: data.buttons.length > 0
                ? data.buttons.map(normalizeButtonItem)
                : [createDefaultFloatingButtonItem()],
        };
    }

    const { text, link, icon, isVisible, position, style } = data;
    return {
        id: data.id,
        ComponentName: data.ComponentName || 'FloatingButton',
        buttons: [normalizeButtonItem({
            text,
            link,
            icon,
            isVisible,
            position,
            style,
        })],
    };
};

class FloatingButton {
    data = {};

    constructor() {
        this.data = {
            id: '9',
            ComponentName: 'FloatingButton',
            buttons: [createDefaultFloatingButtonItem()],
        };
    }

    getId() { return this.data.id; }
    setId(id) { this.data.id = id; }

    getComponentName() { return this.data.ComponentName; }
    setComponentName(ComponentName) { this.data.ComponentName = ComponentName; }

    getButtons() { return this.data.buttons; }
    setButtons(buttons) { this.data.buttons = buttons.map(normalizeButtonItem); }

    getButton(index) {
        return this.data.buttons[index];
    }

    setButton(index, button) {
        if (index >= 0 && index < this.data.buttons.length) {
            this.data.buttons[index] = normalizeButtonItem(button);
        }
    }

    addButton(button = createDefaultFloatingButtonItem()) {
        this.data.buttons.push(normalizeButtonItem(button));
        return this.data.buttons.length - 1;
    }

    removeButton(index) {
        if (this.data.buttons.length <= 1) return false;
        this.data.buttons.splice(index, 1);
        return true;
    }

    moveButton(fromIndex, toIndex) {
        if (fromIndex === toIndex) return false;
        if (fromIndex < 0 || toIndex < 0) return false;
        if (fromIndex >= this.data.buttons.length || toIndex >= this.data.buttons.length) return false;

        const [item] = this.data.buttons.splice(fromIndex, 1);
        this.data.buttons.splice(toIndex, 0, item);
        return true;
    }

    getData() { return this.data; }
    setData(data) {
        const normalized = normalizeFloatingButtonData(data);
        this.data = {
            ...this.data,
            ...normalized,
            buttons: normalized.buttons,
        };
    }

    async load(id) {
        try {
            let response = {};
            let rawData = {};

            if (id) {
                response = await ComponentService.getComponentById(id);
                rawData = response.data ? JSON.parse(response.data) : {};
                this.setId(response?.id);
            } else {
                response = await ComponentService.getComponent(this.data.ComponentName);
                rawData = response[0]?.data ? JSON.parse(response[0]?.data) : {};
                this.setId(response[0]?.id);
            }

            if (Object.keys(rawData).length > 0) {
                this.setData({ ...rawData, id: this.getId() });
            }
            return this.getData();
        } catch (error) {
            throw new Error(error.response?.data);
        }
    }

    async loadByLanguage(language) {
        try {
            const response = await ComponentService.getStaticComponent(this.data.ComponentName, language, true);
            const rawData = response?.data ? JSON.parse(response.data) : {};

            if (Object.keys(rawData).length > 0) {
                this.setData({ ...rawData, id: response?.id });
            }
            return this.getData();
        } catch (error) {
            throw new Error(error.response?.data);
        }
    }

    async save() {
        try {
            const response = await ComponentService.SaveData(this.getId(), this.getData());
            console.log(response);
        } catch (error) {
            throw new Error(error.response?.data);
        }
    }

    renderStyles() {
        this.getButtons().forEach((button, index) => {
            if (!button?.style) return;
            generateDynamicCSS(`FloatingDonateButton-${index}`, [button.style.FloatingDonateButton]);
            generateDynamicCSS(`FloatingDonateButtonHover-${index}`, [button.style.FloatingDonateButtonHover]);
        });
    }
}

export const createFloatingButtonInstance = () => new FloatingButton();
